<template>
  <div class="dropdown-parent-container interactive-tour-container">
    <button
      type="button"
      title="Select a ChartList"
      :id="CHART_LIST_DROPDOWN_MENU_TOGGLE_BUTTON_ID"
      class="dropdown-menu-toggle-arrow-icon-button sc3-button sc3-button-fixed-height sc3-button-white-and-dark-grey"
      :class="{
        'dropdown-menu-toggle-arrow-icon-button-open sc3-button-active': isOpen
      }"
      ref="chartListDropdownMenuButton"
      @click="toggleDropdown()"
    >
      {{ selectedChartListName }}
      &nbsp;
      <i class="fas fa-angle-down"></i>
    </button>
    <DropdownMenu
      :nodes-excluded-from-closing-on-outside-click="chartListDropdownMenuButton ? [chartListDropdownMenuButton] : []"
      :dropdown-button-left-value="left"
      v-model:is-open="isOpen"
      v-model:max-body-height="dropdownHeight"
      :dropdown-menu-toggle-button-node="chartListDropdownMenuButton"
      :include-header-divider="false"
    >
      <template v-slot:header>
        <div class="dropdown-header-container">
          <span class="dropdown-menu-title">YOUR CHARTLISTS</span>
          <button
            v-if="!hasNoChartLists"
            type="button"
            title="Create ChartList"
            class="sc3-button sc3-button-extra-small sc3-button-green "
            @click="[closeDropdown(), launchCreateChartListModal()]"
          >
            <i class="fas fa-plus-circle" aria-hidden="true"></i>&nbsp;&nbsp;Create New ChartList
          </button>
        </div>
        <div class="dropdown-divider"></div>
        <div class="chartlist-search-box-container">
          <i class="fas fa-fw fa-search textbox-search-icon"></i>
          <input
            type="text"
            class="sc3-text-input sc3-text-input-inline chartlist-search-box"
            title="Search"
            placeholder="Search"
            v-model="chartListSearchQuery"
            autocomplete="off"
          />
          <button
            type="button"
            class="clear-search-query-button"
            title="Clear ChartList Search"
            @click="clearSearchQuery"
            v-show="chartListSearchQuery"
          >
            <i class="fa fa-times-circle"></i>
          </button>
        </div>
      </template>
      <template v-slot:body>
        <div v-if="isChartListLoading" class="dropdown-loading-body">
          <i class="fad fa-fw fa-spinner-third fa-spin fa-2x"></i>
        </div>
        <div v-else-if="hasFailedToLoadUserChartLists" class="dropdown-message-body">
          <span class="dropdown-message-small">Failed to load ChartLists.</span>
        </div>
        <div v-else-if="hasNoChartLists" class="dropdown-message-body">
          <span>It looks like you haven't created any<br />ChartLists&nbsp;yet!</span>
          <br />
          <button
            type="button"
            class="sc3-button sc3-button-green"
            @click="[closeDropdown(), launchCreateChartListModal()]"
          >Create ChartList</button>
        </div>
        <div v-else-if="isChartListSearchQueryFilterEmpty" class="dropdown-message-body">
          <span>It looks like you don't have any<br>ChartLists that match your search.</span>
        </div>
        <ul v-else>
          <li
            v-for="chartList in filteredChartLists"
            :class="{ 'selected-dropdown-option': chartList.recid === selectedChartListRecid }"
            :key="chartList.recid"
          >
            <button
              type="button"
              class="ellipsis-overflow"
              :title="chartList.htmlSanitizedName"
              @click="[closeDropdown(), selectChartList(chartList)]"
              v-sanitize-html="chartList.name"
            ></button>
            <a
              title="View All"
              class="icon-button"
              :class="{ 'icon-on-row-hover-only': chartList.recid !== selectedChartListRecid }"
              :href="`/def/servlet/Favorites.CServlet?obj=${chartCode},${chartList.listNum}&cmd=show&disp=e`"
              @click="closeDropdown()"
            ><i class="fas fa-fw fa-eye dark-blue-icon"></i></a>
            <a
              title="Summary View"
              class="icon-button"
              :class="{ 'icon-on-row-hover-only': chartList.recid !== selectedChartListRecid }"
              :href="`/def/servlet/Favorites.CServlet?obj=${chartCode},${chartList.listNum}&cmd=show&disp=b`"
              @click="closeDropdown()"
            ><i class="fal fa-fw fa-table purple-icon"></i></a>
            <a
              title="Edit ChartList"
              class="icon-button"
              :class="{ 'icon-on-row-hover-only': chartList.recid !== selectedChartListRecid }"
              :href="`/def/servlet/Favorites.CServlet?obj=${chartCode},${chartList.listNum}&cmd=show&disp=c`"
              @click="closeDropdown()"
              :disabled="chartList.isReadonly"
            ><i class="fas fa-fw fa-pencil-alt blue-icon"></i></a>
          </li>
        </ul>
      </template>
    </DropdownMenu>
    <InteractiveTourWindow
      :page-number="3"
      :position="{ left: '50%', transform: 'translate(-50%, 0)' }"
      :arrow-position="InteractiveTourTrianglePosition.TopCenter"
    >
      <template v-slot:main-header>
        <span>Access your ChartLists and all of the saved charts within</span>
      </template>
      <template v-slot:explanation>
        <div>
          <p>
            The two stacked menus you'll find above your charts allow you to access your ChartLists and all of your saved charts directly from the workbench.
            Once you've selected a ChartList and a saved chart within it, you can use the left and right arrow buttons to quickly flip through the symbols in that ChartList.
          </p>
        </div>
      </template>
    </InteractiveTourWindow>
  </div>
</template>

<script lang="ts" setup>
  import { computed, ref, useCssVars, watch } from 'vue';
  import { storeToRefs } from 'pinia';
  import { useSavedChartsStore } from '@/stores/savedChartsStore';
  import { useAuthentication } from '@/composables/useAuthentication';
  import { useWorkbenchSelections } from '@/composables/useWorkbenchSelections';
  import { useBoundingClientRect } from '@/composables/useBoundingClientRect';
  import { useModals } from '@/composables/useModals';
  import { InteractiveTourTrianglePosition } from '@/composables/useInteractiveTour';
  import DropdownMenu from '@/components/dropdowns/DropdownMenu.vue';
  import InteractiveTourWindow from '@/components/page-skeleton/InteractiveTourWindow.vue';
  import { HEIGHT_OF_EACH_DROPDOWN_ELEMENT, MAX_AMOUNT_OF_DROPDOWN_ELEMENTS_TO_SHOW } from '@/enums/upper-bar-dropdown-constraints';
  import { CHART_LIST_DROPDOWN_MENU_TOGGLE_BUTTON_ID } from '@/enums/html-ids';

  const savedChartsStore = useSavedChartsStore();
  const { chartLists, isAttemptingToLoadChartLists, hasFailedToLoadChartLists } = storeToRefs(savedChartsStore);
  const { selectedChartList, selectedChartListRecid, selectChartList } = useWorkbenchSelections();
  const { chartCode } = useAuthentication();
  const { getBoundingClientRectPropertyRefs } = useBoundingClientRect();
  const { launchCreateChartListModal } = useModals();
  const chartListSearchQuery = ref('');
  const chartListDropdownMenuButton = ref<HTMLButtonElement>();
  const isOpen = ref<boolean>(false);
  const dropdownHeight = ref<number>(0);
  const { left } = getBoundingClientRectPropertyRefs(chartListDropdownMenuButton, { windowResize: true });
  const filteredChartLists = computed(() => {
    const sanitizedQuery = chartListSearchQuery.value.toLowerCase().trim();
    if (!sanitizedQuery)
      return chartLists.value;

    return chartLists.value.filter((chartList) => {
      return chartList.searchTags.map(tag => tag.toLowerCase()).some(tag => tag.includes(sanitizedQuery));
    });
  });
  const selectedChartListName = computed(() => {
    if (!selectedChartList.value?.name)
      return '-- Select a ChartList --';

    return selectedChartList.value.htmlSanitizedName
  });
  const hasFailedToLoadUserChartLists = computed(() => {
    return !chartLists.value.length && !isAttemptingToLoadChartLists.value && hasFailedToLoadChartLists.value;
  });
  const hasNoChartLists = computed(() => {
    return !chartLists.value.length && !isAttemptingToLoadChartLists.value && !hasFailedToLoadChartLists.value;
  });
  const isChartListLoading = computed(() => {
    return !chartLists.value.length && isAttemptingToLoadChartLists.value;
  });
  const isChartListSearchQueryFilterEmpty = computed(() => {
    return !filteredChartLists.value.length && !isAttemptingToLoadChartLists.value && !hasFailedToLoadChartLists.value;
  });

  function toggleDropdown() {
    isOpen.value = !isOpen.value;
  }

  function closeDropdown() {
    isOpen.value = false;
  }

  function clearSearchQuery() {
    chartListSearchQuery.value = '';
  }

  watch(isOpen, (newOpenValue) => {
    if (!newOpenValue) {
      chartListDropdownMenuButton.value?.blur();
      clearSearchQuery();
    }
  });

  useCssVars(() => {
    return {
      'absolute-max-dropdown-height': `${HEIGHT_OF_EACH_DROPDOWN_ELEMENT * MAX_AMOUNT_OF_DROPDOWN_ELEMENTS_TO_SHOW}px`,
      'screen-size-max-dropdown-height': `${dropdownHeight.value}px`
    };
  });
</script>

<style scoped>
  .dropdown-parent-container {
    position: relative;
    width: 100%;
  }

  .dropdown-header-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-family: 'Roboto Condensed', 'Lato', 'Helvetica Neue', Helvetica, Arial, sans-serif;
    font-size: 16px;
    font-weight: 900;
    color: #707f8f;
    text-align: left;
    padding: 2px 10px;
    margin: 0px;
    gap: 10px;
  }

  .dropdown-header-container button.sc3-button {
    display: flex;
    align-items: center;
    margin: 0;
  }

  div.chartlist-search-box-container {
    position: relative;
    padding: 4px;
  }

  div.chartlist-search-box-container i.textbox-search-icon {
    position: absolute;
    transform: translate(0px, -50%);
    top: 50%;
    font-size: 12px;
    color: #707f8f;
    left: 4px;
    padding: 0px 6px;
  }

  div.chartlist-search-box-container button.clear-search-query-button {
    position: absolute;
    transform: translate(0px, -50%);
    top: 50%;
    font-size: 12px;
    color: #707f8f;
    right: 4px;
    padding: 0px 6px;
    cursor: pointer;
    background: none;
    border: none;
    transition: all 0.35s ease;
  }

  div.chartlist-search-box-container button.clear-search-query-button:hover {
    color: #a9b2bc;
  }

  div.chartlist-search-box-container input.chartlist-search-box {
    width: 100%;
    margin: 0px;
    padding: 0px 20px;
    height: 25px;
    text-overflow: ellipsis;
    border-width: 1px;
  }

  ul {
    max-height: min(
      var(--absolute-max-dropdown-height),
      var(--screen-size-max-dropdown-height)
    );
  }

  .dropdown-message-body {
    padding: 10px;
    font-weight: bold;
    text-align: center;
  }

  .dropdown-message-body .dropdown-message-small {
    font-size: 14px;
  }

  .dropdown-loading-body {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 10px;
  }

  #chart-list-dropdown-menu-toggle-button { /* "chart-list-dropdown-menu-toggle-button" matches the value of the CHART_LIST_DROPDOWN_MENU_TOGGLE_BUTTON_ID html-id enum */
    font-size: 12px;
    width: 100%;
    text-overflow: ellipsis;
    overflow: hidden;
  }

  .no-chart-list-selected #chart-list-dropdown-menu-toggle-button { /* "chart-list-dropdown-menu-toggle-button" matches the value of the CHART_LIST_DROPDOWN_MENU_TOGGLE_BUTTON_ID html-id enum */
    color: #707f8f;
  }
</style>
